/*
 * tiny-sql: a genuine, tiny SQL engine for teaching SQL injection.
 *
 * This is NOT a string matcher. It tokenizes, parses and evaluates a useful
 * subset of SQL over in-memory tables. Authentication bypass EMERGES from
 * actually executing the attacker-controlled query; nothing is compared
 * against a stored "answer".
 *
 * Supported subset:
 *   SELECT <cols|*> FROM <table> [WHERE <expr>] [ORDER BY col [ASC|DESC]] [LIMIT n]
 *   Compound: <select> (UNION [ALL] <select>)* [ORDER BY ...] [LIMIT n]
 *   WHERE:  AND, OR, NOT, parentheses, = != <> < > <= >= LIKE
 *           string literals ('' escape), numbers, column refs, NULL, TRUE/FALSE, ?
 *   Comments: -- to end of line, and /* ... *​/
 *   Multiple statements separated by ;  (stacked queries)
 *   Also (so stacked queries genuinely mutate state):
 *     DELETE FROM <table> [WHERE <expr>]
 *     DROP TABLE <table>
 *
 * Works in Node (module.exports) and the browser (window.TinySQL).
 */
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.TinySQL = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  class SqlError extends Error {
    constructor(msg) { super(msg); this.name = 'SqlError'; }
  }

  // ---------- Tokenizer ----------
  const KEYWORDS = new Set([
    'SELECT', 'FROM', 'WHERE', 'ORDER', 'BY', 'ASC', 'DESC', 'LIMIT',
    'AND', 'OR', 'NOT', 'LIKE', 'UNION', 'ALL', 'NULL', 'TRUE', 'FALSE',
    'DELETE', 'DROP', 'TABLE', 'AS'
  ]);

  function tokenize(sql) {
    const tokens = [];
    let i = 0;
    const n = sql.length;
    const isDigit = c => c >= '0' && c <= '9';
    const isIdentStart = c => /[A-Za-z_]/.test(c);
    const isIdent = c => /[A-Za-z0-9_]/.test(c);

    while (i < n) {
      const c = sql[i];
      if (/\s/.test(c)) { i++; continue; }

      // line comment --
      if (c === '-' && sql[i + 1] === '-') {
        i += 2;
        while (i < n && sql[i] !== '\n') i++;
        continue;
      }
      // block comment /* */
      if (c === '/' && sql[i + 1] === '*') {
        i += 2;
        while (i < n && !(sql[i] === '*' && sql[i + 1] === '/')) i++;
        if (i >= n) throw new SqlError('unterminated block comment');
        i += 2;
        continue;
      }
      // string literal with '' escape
      if (c === "'") {
        let j = i + 1;
        let val = '';
        let closed = false;
        while (j < n) {
          if (sql[j] === "'") {
            if (sql[j + 1] === "'") { val += "'"; j += 2; continue; }
            j++; closed = true; break;
          }
          val += sql[j]; j++;
        }
        if (!closed) throw new SqlError('unterminated string literal (unescaped quote?)');
        tokens.push({ type: 'string', value: val, start: i });
        i = j;
        continue;
      }
      // number
      if (isDigit(c) || (c === '.' && isDigit(sql[i + 1]))) {
        let j = i, num = '';
        while (j < n && (isDigit(sql[j]) || sql[j] === '.')) { num += sql[j]; j++; }
        tokens.push({ type: 'number', value: Number(num), start: i });
        i = j;
        continue;
      }
      // identifier / keyword
      if (isIdentStart(c)) {
        let j = i, id = '';
        while (j < n && isIdent(sql[j])) { id += sql[j]; j++; }
        const up = id.toUpperCase();
        tokens.push({ type: KEYWORDS.has(up) ? 'kw' : 'ident', value: id, up, start: i });
        i = j;
        continue;
      }
      // operators / punctuation
      const two = sql.substr(i, 2);
      if (two === '<=' || two === '>=' || two === '<>' || two === '!=') {
        tokens.push({ type: 'op', value: two, start: i }); i += 2; continue;
      }
      if (c === '=' || c === '<' || c === '>') { tokens.push({ type: 'op', value: c, start: i }); i++; continue; }
      if (c === '(') { tokens.push({ type: 'lparen', start: i }); i++; continue; }
      if (c === ')') { tokens.push({ type: 'rparen', start: i }); i++; continue; }
      if (c === ',') { tokens.push({ type: 'comma', start: i }); i++; continue; }
      if (c === ';') { tokens.push({ type: 'semi', start: i }); i++; continue; }
      if (c === '*') { tokens.push({ type: 'star', start: i }); i++; continue; }
      if (c === '?') { tokens.push({ type: 'param', start: i }); i++; continue; }

      throw new SqlError('unexpected character ' + JSON.stringify(c) + ' at position ' + i);
    }
    tokens.push({ type: 'eof' });
    return tokens;
  }

  // ---------- Parser ----------
  class Parser {
    constructor(tokens) { this.toks = tokens; this.pos = 0; this.paramCount = 0; }
    peek(k = 0) { return this.toks[this.pos + k]; }
    next() { return this.toks[this.pos++]; }
    isKw(up) { const t = this.peek(); return t.type === 'kw' && t.up === up; }
    eatKw(up) { if (this.isKw(up)) { this.pos++; return true; } return false; }
    expectKw(up) { if (!this.eatKw(up)) throw new SqlError('expected ' + up + ' but found ' + this._desc()); }
    expect(type) { const t = this.peek(); if (t.type !== type) throw new SqlError('expected ' + type + ' but found ' + this._desc()); return this.next(); }
    _desc() { const t = this.peek(); return t.type === 'eof' ? '<end>' : (t.value !== undefined ? JSON.stringify(t.value) : t.type); }

    parseProgram() {
      const stmts = [];
      // allow leading empty
      while (this.peek().type !== 'eof') {
        if (this.peek().type === 'semi') { this.next(); continue; }
        stmts.push(this.parseStatement());
        // statements separated by ; ; trailing ; allowed
        if (this.peek().type === 'semi') { this.next(); }
        else if (this.peek().type !== 'eof') {
          throw new SqlError('unexpected token after statement: ' + this._desc());
        }
      }
      return stmts;
    }

    parseStatement() {
      const t = this.peek();
      if (t.type === 'kw' && t.up === 'SELECT') return this.parseCompoundSelect();
      if (t.type === 'kw' && t.up === 'DELETE') return this.parseDelete();
      if (t.type === 'kw' && t.up === 'DROP') return this.parseDrop();
      throw new SqlError('unsupported statement starting with ' + this._desc());
    }

    parseCompoundSelect() {
      const cores = [this.parseSelectCore()];
      const ops = [];
      while (this.isKw('UNION')) {
        this.next();
        const all = this.eatKw('ALL');
        ops.push(all ? 'all' : 'distinct');
        cores.push(this.parseSelectCore());
      }
      let orderBy = null, limit = null;
      if (this.isKw('ORDER')) { this.next(); this.expectKw('BY'); orderBy = this.parseOrderBy(); }
      if (this.eatKw('LIMIT')) { orderBy = orderBy; limit = this.parseLimit(); }
      return { type: 'select', cores, ops, orderBy, limit };
    }

    parseSelectCore() {
      this.expectKw('SELECT');
      const columns = this.parseSelectList();
      let from = null, where = null, orderBy = null, limit = null;
      if (this.eatKw('FROM')) from = this.expect('ident').value;
      if (this.eatKw('WHERE')) where = this.parseExpr();
      // ORDER BY / LIMIT bound to a core (SQLite allows only on last, we are lenient)
      if (this.isKw('ORDER')) { this.next(); this.expectKw('BY'); orderBy = this.parseOrderBy(); }
      if (this.eatKw('LIMIT')) limit = this.parseLimit();
      return { columns, from, where, orderBy, limit };
    }

    parseOrderBy() {
      const col = this.expect('ident').value;
      let dir = 'asc';
      if (this.eatKw('ASC')) dir = 'asc';
      else if (this.eatKw('DESC')) dir = 'desc';
      return { column: col, dir };
    }

    parseLimit() {
      const t = this.peek();
      if (t.type === 'number') { this.next(); return t.value; }
      throw new SqlError('expected number after LIMIT but found ' + this._desc());
    }

    parseSelectList() {
      const cols = [];
      // SELECT * (only when it's the whole list)
      if (this.peek().type === 'star') {
        this.next();
        return [{ star: true }];
      }
      do {
        const expr = this.parseExpr();
        let alias = null;
        if (this.eatKw('AS')) alias = this.expect('ident').value;
        else if (this.peek().type === 'ident') { alias = this.next().value; }
        cols.push({ expr, alias });
      } while (this.peek().type === 'comma' && this.next());
      return cols;
    }

    parseDelete() {
      this.expectKw('DELETE');
      this.expectKw('FROM');
      const from = this.expect('ident').value;
      let where = null;
      if (this.eatKw('WHERE')) where = this.parseExpr();
      return { type: 'delete', from, where };
    }

    parseDrop() {
      this.expectKw('DROP');
      this.expectKw('TABLE');
      const table = this.expect('ident').value;
      return { type: 'drop', table };
    }

    // expression grammar (lowest -> highest precedence):
    // orExpr := andExpr (OR andExpr)*
    // andExpr := notExpr (AND notExpr)*
    // notExpr := NOT notExpr | compExpr
    // compExpr := primary ((= != <> < > <= >= | LIKE) primary)?
    // primary := ( expr ) | string | number | NULL | TRUE | FALSE | column | ?
    parseExpr() { return this.parseOr(); }

    parseOr() {
      let left = this.parseAnd();
      while (this.isKw('OR')) { this.next(); const right = this.parseAnd(); left = { type: 'or', left, right }; }
      return left;
    }
    parseAnd() {
      let left = this.parseNot();
      while (this.isKw('AND')) { this.next(); const right = this.parseNot(); left = { type: 'and', left, right }; }
      return left;
    }
    parseNot() {
      if (this.isKw('NOT')) { this.next(); return { type: 'not', expr: this.parseNot() }; }
      return this.parseComparison();
    }
    parseComparison() {
      let left = this.parsePrimary();
      const t = this.peek();
      if (t.type === 'op') {
        this.next();
        const right = this.parsePrimary();
        return { type: 'cmp', op: t.value, left, right };
      }
      if (t.type === 'kw' && t.up === 'LIKE') {
        this.next();
        const right = this.parsePrimary();
        return { type: 'like', left, right };
      }
      return left;
    }
    parsePrimary() {
      const t = this.peek();
      if (t.type === 'lparen') {
        this.next();
        const e = this.parseExpr();
        this.expect('rparen');
        return e;
      }
      if (t.type === 'string') { this.next(); return { type: 'lit', value: t.value }; }
      if (t.type === 'number') { this.next(); return { type: 'lit', value: t.value }; }
      if (t.type === 'param') { this.next(); return { type: 'param', index: this.paramCount++ }; }
      if (t.type === 'kw' && t.up === 'NULL') { this.next(); return { type: 'lit', value: null }; }
      if (t.type === 'kw' && t.up === 'TRUE') { this.next(); return { type: 'lit', value: true }; }
      if (t.type === 'kw' && t.up === 'FALSE') { this.next(); return { type: 'lit', value: false }; }
      if (t.type === 'ident') { this.next(); return { type: 'col', name: t.value }; }
      throw new SqlError('unexpected token in expression: ' + this._desc());
    }
  }

  function parse(sql) {
    const p = new Parser(tokenize(sql));
    const stmts = p.parseProgram();
    return { statements: stmts, paramCount: p.paramCount };
  }

  // ---------- Evaluator ----------
  // three-valued truth: returns true | false | null(unknown)
  function truth(v) {
    if (v === null || v === undefined) return null;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'string') { const num = parseFloat(v); return isNaN(num) ? false : num !== 0; }
    return !!v;
  }

  function compare(op, a, b) {
    if (a === null || a === undefined || b === null || b === undefined) return null; // unknown
    let cmp;
    if (typeof a === 'number' && typeof b === 'number') cmp = a - b;
    else cmp = String(a) < String(b) ? -1 : (String(a) > String(b) ? 1 : 0);
    switch (op) {
      case '=': return cmp === 0;
      case '!=':
      case '<>': return cmp !== 0;
      case '<': return cmp < 0;
      case '>': return cmp > 0;
      case '<=': return cmp <= 0;
      case '>=': return cmp >= 0;
    }
    throw new SqlError('unknown operator ' + op);
  }

  function likeToRegex(pattern) {
    let out = '';
    for (const ch of String(pattern)) {
      if (ch === '%') out += '.*';
      else if (ch === '_') out += '.';
      else out += ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    return new RegExp('^' + out + '$', 'i'); // LIKE is case-insensitive (SQLite ASCII default)
  }

  function getCol(rowObj, name) {
    if (name in rowObj) return rowObj[name];
    const lower = name.toLowerCase();
    for (const k of Object.keys(rowObj)) if (k.toLowerCase() === lower) return rowObj[k];
    throw new SqlError('no such column: ' + name);
  }

  function evalExpr(node, rowObj, params) {
    switch (node.type) {
      case 'lit': return node.value;
      case 'param': return params[node.index];
      case 'col': return getCol(rowObj, node.name);
      case 'and': {
        const a = truth(evalExpr(node.left, rowObj, params));
        const b = truth(evalExpr(node.right, rowObj, params));
        if (a === false || b === false) return false;
        if (a === null || b === null) return null;
        return true;
      }
      case 'or': {
        const a = truth(evalExpr(node.left, rowObj, params));
        const b = truth(evalExpr(node.right, rowObj, params));
        if (a === true || b === true) return true;
        if (a === null || b === null) return null;
        return false;
      }
      case 'not': {
        const a = truth(evalExpr(node.expr, rowObj, params));
        return a === null ? null : !a;
      }
      case 'cmp': return compare(node.op, evalExpr(node.left, rowObj, params), evalExpr(node.right, rowObj, params));
      case 'like': {
        const l = evalExpr(node.left, rowObj, params);
        if (l === null || l === undefined) return null;
        return likeToRegex(evalExpr(node.right, rowObj, params)).test(String(l));
      }
    }
    throw new SqlError('cannot evaluate node ' + node.type);
  }

  function getTable(db, name) {
    if (db.tables[name]) return db.tables[name];
    const lower = name.toLowerCase();
    for (const k of Object.keys(db.tables)) if (k.toLowerCase() === lower) return db.tables[k];
    throw new SqlError('no such table: ' + name);
  }

  // execute a single SELECT core -> { columns:[names], rows:[[values...]] }
  function execCore(core, db, params) {
    let sourceRows, sourceCols;
    if (core.from) {
      const t = getTable(db, core.from);
      sourceCols = t.columns.slice();
      sourceRows = t.rows.map(r => Object.assign({}, r));
    } else {
      sourceCols = [];
      sourceRows = [{}]; // one synthetic row (SELECT with no FROM)
    }

    // WHERE
    let filtered = sourceRows;
    if (core.where) filtered = filtered.filter(r => truth(evalExpr(core.where, r, params)) === true);

    // per-core ORDER BY runs on the SOURCE rows, so it may reference a column
    // that is not in the SELECT list (real SQLite behaviour).
    if (core.orderBy) filtered = applyOrderObjects(filtered, core.orderBy);

    // projection
    let outCols, outRows;
    if (core.columns.length === 1 && core.columns[0].star) {
      outCols = sourceCols.slice();
      outRows = filtered.map(r => outCols.map(c => r[c]));
    } else {
      outCols = core.columns.map((c, idx) => {
        if (c.alias) return c.alias;
        if (c.expr.type === 'col') return c.expr.name;
        return 'col' + (idx + 1);
      });
      outRows = filtered.map(r => core.columns.map(c => evalExpr(c.expr, r, params)));
    }

    // per-core LIMIT
    if (core.limit != null) outRows = outRows.slice(0, core.limit);
    return { columns: outCols, rows: outRows };
  }

  function cmpValues(a, b, sign) {
    if (a === b) return 0;
    if (a === null || a === undefined) return -1 * sign;
    if (b === null || b === undefined) return 1 * sign;
    let c;
    if (typeof a === 'number' && typeof b === 'number') c = a - b;
    else c = String(a) < String(b) ? -1 : 1;
    return c * sign;
  }

  // order an array of row OBJECTS (full source columns available)
  function applyOrderObjects(rows, orderBy) {
    const sign = orderBy.dir === 'desc' ? -1 : 1;
    return rows.slice().sort((ra, rb) => cmpValues(getCol(ra, orderBy.column), getCol(rb, orderBy.column), sign));
  }

  function applyOrder(rows, cols, orderBy) {
    const idx = cols.findIndex(c => c.toLowerCase() === orderBy.column.toLowerCase());
    if (idx < 0) throw new SqlError('no such column in ORDER BY: ' + orderBy.column);
    const sign = orderBy.dir === 'desc' ? -1 : 1;
    return rows.slice().sort((ra, rb) => {
      const a = ra[idx], b = rb[idx];
      if (a === b) return 0;
      if (a === null || a === undefined) return -1 * sign;
      if (b === null || b === undefined) return 1 * sign;
      let c;
      if (typeof a === 'number' && typeof b === 'number') c = a - b;
      else c = String(a) < String(b) ? -1 : 1;
      return c * sign;
    });
  }

  function execSelect(stmt, db, params) {
    let result = execCore(stmt.cores[0], db, params);
    for (let k = 1; k < stmt.cores.length; k++) {
      const right = execCore(stmt.cores[k], db, params);
      if (right.columns.length !== result.columns.length) {
        throw new SqlError('SELECTs to the left and right of UNION do not have the same number of result columns');
      }
      result = { columns: result.columns, rows: result.rows.concat(right.rows) };
      if (stmt.ops[k - 1] === 'distinct') {
        const seen = new Set();
        result.rows = result.rows.filter(r => {
          const key = JSON.stringify(r);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }
    }
    if (stmt.orderBy) result.rows = applyOrder(result.rows, result.columns, stmt.orderBy);
    if (stmt.limit != null) result.rows = result.rows.slice(0, stmt.limit);
    return result;
  }

  function execStatement(stmt, db, params) {
    if (stmt.type === 'select') {
      const r = execSelect(stmt, db, params);
      // present as array of objects too
      const objects = r.rows.map(row => {
        const o = {};
        r.columns.forEach((c, i) => { o[c] = row[i]; });
        return o;
      });
      return { kind: 'select', columns: r.columns, rows: r.rows, objects };
    }
    if (stmt.type === 'delete') {
      const t = getTable(db, stmt.from);
      const before = t.rows.length;
      if (stmt.where) t.rows = t.rows.filter(r => truth(evalExpr(stmt.where, r, params)) !== true);
      else t.rows = [];
      return { kind: 'delete', deleted: before - t.rows.length };
    }
    if (stmt.type === 'drop') {
      const key = Object.keys(db.tables).find(k => k.toLowerCase() === stmt.table.toLowerCase());
      if (!key) throw new SqlError('no such table: ' + stmt.table);
      delete db.tables[key];
      return { kind: 'drop', table: stmt.table };
    }
    throw new SqlError('unsupported statement type ' + stmt.type);
  }

  /**
   * Execute a program (one or more ; separated statements).
   * @param {string} sql
   * @param {object} db  { tables: { name: { columns:[], rows:[{...}] } } }
   * @param {Array}  params  positional values bound to `?` placeholders
   * @returns {Array} one result per statement
   */
  function execute(sql, db, params = []) {
    const { statements } = parse(sql);
    const results = [];
    for (const stmt of statements) results.push(execStatement(stmt, db, params));
    return results;
  }

  function makeDb(tables) {
    // deep-ish clone so callers can reset between runs
    const out = { tables: {} };
    for (const [name, def] of Object.entries(tables)) {
      out.tables[name] = { columns: def.columns.slice(), rows: def.rows.map(r => Object.assign({}, r)) };
    }
    return out;
  }

  return { tokenize, parse, execute, makeDb, SqlError };
});
